import { ServerNode } from './WorkflowBuilder.types';

export interface AutoInboundPrompt {
    promptId: string;
    promptName: string;
}

export const AUTO_INBOUND_PROMPT_LABEL = 'Auto (first call node prompt)';
export const NO_INBOUND_PROMPT_WARNING = 'No call node with a prompt. Inbound calls will have no prompt';

const nextNodeIds = (node: ServerNode): string[] => {
    const { nextNode, conditions, defaultNextNode } = node.config ?? {};
    const ids: string[] = [];
    if (nextNode) ids.push(nextNode);
    (conditions ?? []).forEach((condition) => {
        if (condition?.nextNode) ids.push(condition.nextNode);
    });
    if (defaultNextNode) ids.push(defaultNextNode);
    return ids;
};

/**
 * The prompt inbound calls get when the workflow is left on Auto: the nearest
 * CALL node with a prompt, found by BFS from the head node.
 *
 * Mirrors the backend's firstCallNodePromptID (app/repositories/inbound_prompt_walk.go)
 * so the builder predicts what the dispatch rule will carry. Both walk the
 * serialized workflow_definition nodes (convertToServerNodes output), following
 * config.nextNode, then config.conditions[].nextNode in array order, then
 * config.defaultNextNode. Only CALL nodes count; ids that don't resolve are
 * skipped and a visited set stops cycles.
 */
export const getAutoInboundPrompt = (
    nodes: readonly ServerNode[],
    headNodeId: string | null | undefined
): AutoInboundPrompt | null => {
    if (!headNodeId) return null;

    const byId = new Map(nodes.map((node) => [node.id, node]));
    const visited = new Set<string>();
    const queue: string[] = [headNodeId];

    while (queue.length > 0) {
        const id = queue.shift() as string;
        if (visited.has(id)) continue;
        visited.add(id);

        const node = byId.get(id);
        if (!node) continue;

        if (node.type === 'CALL') {
            // Same key order and trimming as the backend's promptIDFromNodeConfig.
            const promptId = node.config?.promptId?.trim() || node.config?.langfusePromptId?.trim() || '';
            if (promptId) {
                return { promptId, promptName: node.config?.promptName || '' };
            }
        }

        nextNodeIds(node).forEach((next) => {
            if (!visited.has(next)) queue.push(next);
        });
    }

    return null;
};
